/**
 * TenantOverviewTab — Süperadmin merchant detay > Genel Bakış sekmesi.
 *
 * 4 KPI + 3 sağlık sinyali (head script, trafik, XML).
 */
import { Progress } from "@/components/ui/progress";
import { Card, Eyebrow } from "@/components/design-system";
import {
    Globe, Package, Boxes, Users,
    FileCode2, Activity, FileText,
    CheckCircle2, AlertTriangle, XCircle, ArrowRightCircle,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { relTime } from "../_tenant-helpers";

export default function TenantOverviewTab({ data, onJumpTab }) {
    const lic = data.license || {};
    const traffic = data.traffic || {};
    const inst = data.install || {};
    const xml = data.xml || {};
    const catalog = data.catalog || {};

    const kpis = [
        {
            icon: Users,
            label: "Takım",
            value: `${data.user_count || 0}`,
            sub: "aktif kullanıcı",
            testId: "kpi-users",
        },
        {
            icon: Package,
            label: "Lisans",
            value: lic.is_expired
                ? "Süresi bitti"
                : lic.days_remaining != null
                    ? `${lic.days_remaining} gün`
                    : "—",
            sub: lic.status === "trial" ? "deneme kalan" : "yenilemeye",
            testId: "kpi-license",
            tone: lic.is_expired ? "danger" : (lic.days_remaining != null && lic.days_remaining <= 3) ? "warn" : null,
        },
        {
            icon: Activity,
            label: "Trafik",
            value: `${traffic.usage_this_month ?? 0}`,
            sub: `/ ${traffic.quota_this_month ?? 0} bu ay`,
            testId: "kpi-traffic",
            tone: (traffic.usage_pct || 0) >= 80 ? "warn" : null,
        },
        {
            icon: Boxes,
            label: "Katalog",
            value: `${catalog.products_count || 0}`,
            sub: `ürün · ${catalog.tables_count || 0} tablo`,
            testId: "kpi-catalog",
        },
    ];

    return (
        <div className="space-y-5" data-testid="overview-tab">
            {/* KPIs */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {kpis.map((k) => (
                    <KPIBlock key={k.label} {...k} />
                ))}
            </div>

            {/* Sağlık sinyalleri */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <HealthSignal
                    icon={FileCode2}
                    title="Head Script"
                    status={inst.head_status}
                    detail={inst.head_last_at ? `Son kontrol ${relTime(inst.head_last_at)}` : "Henüz test edilmedi"}
                    note={inst.head_reason}
                    testId="signal-head"
                    cta={{ label: "Entegrasyonu Aç", onClick: () => onJumpTab?.("integration") }}
                />
                <HealthSignal
                    icon={Activity}
                    title="Trafik Kullanımı"
                    status={signalFromPct(traffic.usage_pct || 0)}
                    detail={`%${(traffic.usage_pct || 0).toFixed(1)} doluluk`}
                    testId="signal-traffic"
                    cta={{ label: "Trafiği İncele", onClick: () => onJumpTab?.("traffic") }}
                    extra={
                        <Progress
                            value={Math.min(100, traffic.usage_pct || 0)}
                            className={`h-1.5 mt-2 ${(traffic.usage_pct || 0) >= 100 ? "[&>div]:bg-rose-500" : (traffic.usage_pct || 0) >= 80 ? "[&>div]:bg-amber-500" : "[&>div]:bg-emerald-500"}`}
                        />
                    }
                />
                <HealthSignal
                    icon={FileText}
                    title="XML Senkron"
                    status={xmlSignal(xml)}
                    detail={xml.last_sync_at
                        ? `${relTime(xml.last_sync_at)} · ${xml.total_products || 0} ürün`
                        : xml.url ? "Hiç çalışmadı" : "URL girilmedi"}
                    testId="signal-xml"
                    cta={{ label: "Zamanlamayı Aç", onClick: () => onJumpTab?.("integration") }}
                />
            </div>
        </div>
    );
}

function signalFromPct(pct) {
    if (pct >= 100) return "disconnected";
    if (pct >= 80) return "warn";
    return "connected";
}

function xmlSignal(xml) {
    if (!xml.url) return "unknown";
    if (xml.last_sync_status === "failed") return "disconnected";
    if (xml.hours_since_sync != null && xml.hours_since_sync > 48) return "warn";
    if (xml.last_sync_at) return "connected";
    return "warn";
}

function KPIBlock({ icon: Icon, label, value, sub, tone, testId }) {
    const valueCls =
        tone === "danger" ? "text-rose-600" :
        tone === "warn"   ? "text-amber-600" :
                            "text-foreground";
    return (
        <Card className="p-4" data-testid={testId}>
            <div className="flex items-center justify-between mb-2">
                <Eyebrow>{label}</Eyebrow>
                <Icon className="h-3.5 w-3.5 text-muted-foreground" strokeWidth={1.75} />
            </div>
            <div className={`font-display text-xl font-bold tracking-tight ${valueCls}`}>
                {value}
            </div>
            <div className="text-[11px] text-muted-foreground mt-0.5">{sub}</div>
        </Card>
    );
}

function HealthSignal({ icon: Icon, title, status, detail, note, cta, extra, testId }) {
    const map = {
        connected:    { color: "bg-emerald-500", ring: "ring-emerald-500/30", label: "Sağlıklı", IconC: CheckCircle2, iconCls: "text-emerald-600" },
        warn:         { color: "bg-amber-500",   ring: "ring-amber-500/30",   label: "Dikkat",   IconC: AlertTriangle, iconCls: "text-amber-600" },
        disconnected: { color: "bg-rose-500",    ring: "ring-rose-500/30",    label: "Kritik",   IconC: XCircle, iconCls: "text-rose-600" },
        unknown:      { color: "bg-muted-foreground/30", ring: "ring-muted/30", label: "Bilinmiyor", IconC: AlertTriangle, iconCls: "text-muted-foreground" },
    };
    const m = map[status] || map.unknown;
    const IconC = m.IconC;
    return (
        <Card className="p-4" data-testid={testId}>
            <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                    <div className="flex items-center gap-1.5">
                        <Icon className="h-3.5 w-3.5 text-muted-foreground" strokeWidth={1.75} />
                        <span className="text-[11px] font-semibold tracking-wider uppercase text-muted-foreground">
                            {title}
                        </span>
                    </div>
                    <div className="mt-2 flex items-center gap-1.5">
                        <IconC className={`h-3.5 w-3.5 ${m.iconCls}`} strokeWidth={2} />
                        <span className="text-sm font-medium">{m.label}</span>
                    </div>
                    <div className="text-[11px] text-muted-foreground mt-0.5 leading-relaxed">
                        {detail}
                    </div>
                    {note && (
                        <div className="text-[11px] text-muted-foreground/80 italic mt-1">
                            {note}
                        </div>
                    )}
                </div>
                <span
                    aria-hidden
                    className={`inline-block h-2 w-2 rounded-full ring-4 shrink-0 mt-1 ${m.color} ${m.ring}`}
                />
            </div>
            {extra}
            {cta && (
                <Button
                    variant="outline"
                    size="sm"
                    onClick={cta.onClick}
                    className="h-7 text-[11px] mt-3"
                    data-testid={`${testId}-cta`}
                >
                    {cta.label}
                    <ArrowRightCircle className="h-3 w-3 ml-1" strokeWidth={1.75} />
                </Button>
            )}
        </Card>
    );
}
